/**
 * Skills API
 * 
 * API methods for managing skills.
 */

import { apiClient } from './client';
import type {
  Skill,
  SkillListResponse,
  CreateSkillRequest,
  UpdateSkillConfigRequest,
} from './types';

/**
 * List all available skills for an organization
 */
export async function listSkills(
  organizationId: string
): Promise<SkillListResponse> {
  return apiClient.get<SkillListResponse>(`/skills?organization_id=${organizationId}`);
}

/**
 * Create a new custom skill (admin only)
 */
export async function createSkill(
  organizationId: string,
  data: CreateSkillRequest
): Promise<{ skill: Skill }> {
  return apiClient.post<{ skill: Skill }>(`/skills?organization_id=${organizationId}`, data);
}

/**
 * Get a specific skill
 */
export async function getSkill(
  skillId: string,
  organizationId: string
): Promise<{ skill: Skill }> {
  return apiClient.get<{ skill: Skill }>(`/skills/${skillId}?organization_id=${organizationId}`);
}

/**
 * Update skill configuration (admin only)
 */
export async function updateSkillConfig(
  skillId: string,
  organizationId: string,
  data: UpdateSkillConfigRequest
): Promise<{ skill: Skill }> {
  return apiClient.put<{ skill: Skill }>(`/skills/${skillId}/config?organization_id=${organizationId}`, data);
}

/**
 * Enable a skill for organization (admin only)
 */
export async function enableSkill(
  skillId: string,
  organizationId: string
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(`/skills/${skillId}/enable?organization_id=${organizationId}`, {});
}

/**
 * Disable a skill for organization (admin only)
 */
export async function disableSkill(
  skillId: string,
  organizationId: string
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(`/skills/${skillId}/disable?organization_id=${organizationId}`, {});
}

/**
 * Delete a custom skill (admin only)
 */
export async function deleteSkill(
  skillId: string,
  organizationId: string
): Promise<{ success: boolean; message: string }> {
  return apiClient.delete<{ success: boolean; message: string }>(`/skills/${skillId}?organization_id=${organizationId}`);
}
